import { useEffect, useRef, useState } from "react";

/**
 * Network connectivity status as seen by the browser.
 *
 * - `online`   — Normal connectivity
 * - `offline`  — navigator.onLine is false
 * - `recovered` — Just came back online (auto-dismisses after 2s)
 */
export type NetworkStatus = "online" | "offline" | "recovered";

interface UseNetworkStatusResult {
  status: NetworkStatus;
}

const RECOVERY_DISPLAY_MS = 2000;
const CONNECTIVITY_PROBE_PATH = "/favicon.svg";

/**
 * Tracks browser online/offline state and provides a recovery
 * transition so the UI can show a brief "Back online!" banner.
 *
 * Detection via `navigator.onLine` + window online/offline events.
 *
 * @example
 * const { status } = useNetworkStatus();
 * if (status === 'offline') return <OfflineBanner />;
 */
export function useNetworkStatus(): UseNetworkStatusResult {
  // Start as "online" to avoid a flash of the offline banner on initial load.
  // The effect below will verify connectivity if navigator.onLine reports false,
  // transitioning to "offline" only after confirmation.
  const [status, setStatus] = useState<NetworkStatus>("online");
  const wasOfflineRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const verifyConnectivity = async () => {
      if (navigator.onLine) {
        return;
      }

      try {
        const response = await fetch(CONNECTIVITY_PROBE_PATH, {
          method: "HEAD",
          cache: "no-store",
        });

        if (!cancelled && response.ok) {
          wasOfflineRef.current = false;
          setStatus("online");
        }
      } catch {
        // Probe failed and navigator says offline — now safe to show the bar
        if (!cancelled) {
          wasOfflineRef.current = true;
          setStatus("offline");
        }
      }
    };

    const handleOffline = () => {
      wasOfflineRef.current = true;
      setStatus("offline");
      // Cancel any pending recovery timer
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const handleOnline = () => {
      if (wasOfflineRef.current) {
        // Was offline — show recovery state
        setStatus("recovered");
        timerRef.current = setTimeout(() => {
          setStatus("online");
          timerRef.current = null;
        }, RECOVERY_DISPLAY_MS);
      }
      // If was never offline, stay online (no change needed)
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    void verifyConnectivity();

    return () => {
      cancelled = true;
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return { status };
}
